import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useUser } from '../context/UserContext';

const LOADING_TIMEOUT_MS = 10000;

/**
 * RequireSession — Guard de sesión para rutas protegidas.
 * - Sin sesión → redirige a /login
 * - Cargando  → muestra spinner (con timeout de seguridad)
 * - Sesión OK → render children
 */
export default function RequireSession({ children }) {
  const { session, loading, profileLoading } = useAuth();
  const { user } = useUser();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (loading && !session) {
      setTimedOut(false);
      const timer = setTimeout(() => {
        console.warn('[RequireSession] ⚠️ Loading timed out after', LOADING_TIMEOUT_MS, 'ms');
        setTimedOut(true);
      }, LOADING_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
    if (session) setTimedOut(false);
  }, [loading, session]);

  // Aún cargando (con timeout)
  if (loading && !session && !timedOut) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <span className="text-gray-400 text-sm">Cargando sesión…</span>
      </div>
    );
  }

  // Sin sesión → redirect
  if (!session) {
    console.warn('[RequireSession] No session, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Guard de trial expirado: esperar a que el perfil esté completamente cargado
  // (durante bootstrap, UserContext entrega trialDaysLeft:7 como fallback — no evaluar antes)
  if (!profileLoading && user) {
    const isExpiredTrial =
      user.plan === 'trial' && (user.trialDaysLeft ?? 1) <= 0;
    const isPlansPage = location.pathname === '/plans';

    if (isExpiredTrial && !isPlansPage) {
      console.warn('[RequireSession] Trial expirado, redirigiendo a /plans');
      return <Navigate to="/plans" replace />;
    }
  }

  // Fallback: si profileLoading nunca resuelve (requests abortadas por navegación),
  // usar cache de localStorage para detectar trial expirado sin esperar al perfil.
  if (profileLoading && session?.user?.id) {
    const isPlansPage = location.pathname === '/plans';
    if (!isPlansPage) {
      try {
        const uid = session.user.id;
        const cachedPlan = localStorage.getItem(`iv1_last_plan:${uid}`);
        const cachedEnds = localStorage.getItem(`iv1_trial_ends:${uid}`);
        if (cachedPlan === 'trial' && cachedEnds) {
          const daysLeft = Math.ceil((new Date(cachedEnds).getTime() - Date.now()) / 86400000);
          if (Number.isFinite(daysLeft) && daysLeft <= 0) {
            console.warn('[RequireSession] Trial expirado (cache fallback), redirigiendo a /plans');
            return <Navigate to="/plans" replace />;
          }
        }
      } catch { /* localStorage puede fallar en algunos navegadores */ }
    }
  }

  return children;
}
